<template>
  <div class="page fill-height d-flex flex-column gap-6" :class="{ 'pt-4': currentStep !== 3 }">
    <loading-overlay v-if="loading" :message="loadingMessage" show></loading-overlay>
    <location-permission-dialog
      :visible="askingForLocation"
      @agree="retrieveLocation()"
      @disagree="onNoGpsAllowed('denied')"></location-permission-dialog>

    <comet-navbar primary class="px-4" v-if="currentStep !== 3">
      <language-selector v-if="started" />
    </comet-navbar>
    <div v-if="!started" class="comet-i18n-exclude d-flex flex-column gap-6 px-4 my-auto">
      <language-selector />
      <h1 class="d-flex flex-column gap-2">
        <span class="welcome">{{ $t('initial.welcome') }}</span>
        <span class="title globalseo-exclude">{{ warehouseData.name }}</span>
      </h1>
      <p class="instructions">{{ $t('initial.instructions') }}</p>
      <v-btn
        block
        append-icon="mdi-arrow-right"
        color="primary"
        :disabled="!location.settled"
        @click="handleStart()">
        {{ $t('initial.startCheckIn') }}
      </v-btn>
    </div>

    <appointment-form
      v-if="started"
      :latitude="String(location.latitude)"
      :longitude="String(location.longitude)"
      :warehouse="warehouseData"
      @step-change="currentStep = $event"
      @back="onAppointmentBack"></appointment-form>

    <i18n-t
      v-if="!started && isCometBffEnabled"
      keypath="initial.recaptchaAttribution"
      tag="p"
      class="recaptcha-attribution">
      <template #privacyPolicy>
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
          {{ $t('initial.recaptchaPrivacyPolicy') }}
        </a>
      </template>
      <template #termsOfService>
        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
          {{ $t('initial.recaptchaTermsOfService') }}
        </a>
      </template>
    </i18n-t>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import axios from 'axios';
import {
  clearLocalStorageKey,
  updateObjectInLocalStorage
} from '@/components/mixins/localStorageMixin';
import type { IWarehouse } from '@nova/core';

import retrieveLocationMixin from '@/components/mixins/retrieveLocationMixin';
import cometApiService from '@/services/cometApiService';
import LanguageSelector from '@/components/LanguageSelector.vue';
import CometNavbar from '@/components/CometNavbar.vue';
import { useFlag } from '@loadsmart/vue-feature-flags';

export default defineComponent({
  components: {
    LanguageSelector,
    CometNavbar
  },
  mixins: [retrieveLocationMixin],
  setup() {
    const { getWarehouse } = cometApiService();
    const bffFlag = useFlag('enable-comet-bff');
    const isCometBffEnabled = computed(() => Boolean(bffFlag.value?.enabled));
    return { getWarehouse, isCometBffEnabled };
  },
  async mounted() {
    clearLocalStorageKey('comet:appointment');
    clearLocalStorageKey('comet:warehouse');
    clearLocalStorageKey('comet:misc');
    this.verifyWarehouse();

    if (this.warehouseId) {
      await this.setWarehouse();
      if (this.warehouseData?.id) {
        if (this.warehouseData.settings?.gateManagementRequiresGeofencing) {
          await this.askForLocation();
        } else {
          this.onLocationSuccess();
        }

        // Set on global store
        if (this.warehouseData.settings?.enableMilitaryTime === true) {
          await this.$store.dispatch('setMilitaryTime', true);
        }
      }
    }
  },
  data() {
    return {
      started: false,
      currentStep: null as number | null,
      retrievingWarehouse: false,
      warehouseId: '',
      warehouseData: {} as IWarehouse
    };
  },
  computed: {
    loading() {
      return this.calculatingLocation || this.retrievingWarehouse;
    },
    loadingMessage() {
      if (this.calculatingLocation) {
        return this.$t('common.loading.retrievingLocation');
      } else if (this.retrievingWarehouse) {
        return this.$t('common.loading.retrievingWarehouse');
      }
      return '';
    }
  },
  methods: {
    onAppointmentBack() {
      this.started = false;
      this.currentStep = null;
    },
    handleStart() {
      this.started = true;
    },
    verifyWarehouse() {
      this.warehouseId = (this.$route.query.warehouseId as string) ?? '';

      if (!this.warehouseId) {
        return this.$router.push({
          name: 'warehouse-error',
          query: {
            icon: 'mdi-alert',
            title: this.$t('initial.missingWarehouseTitle'),
            message: this.$t('initial.missingWarehouseMessage')
          }
        });
      }
    },
    async onLocationSuccess() {
      this.location.settled = true;
    },
    async setWarehouse() {
      try {
        this.retrievingWarehouse = true;

        const data = await this.getWarehouse(this.warehouseId);
        this.warehouseData = { ...data };
        updateObjectInLocalStorage('comet:warehouse', this.warehouseData);
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const errorParams = this.makeErrorScreenParams(err);

          const responseMessage = err.response?.data?.message;
          const messageStr = Array.isArray(responseMessage) ? responseMessage[0] : responseMessage;
          const isRecaptchaError = typeof messageStr === 'string' && /reCAPTCHA/iu.test(messageStr);

          this.$router.push({
            name: isRecaptchaError ? 'verification-error' : 'warehouse-error',
            query: errorParams
          });
        } else {
          this.$router.push({
            name: 'warehouse-error',
            query: {
              icon: 'mdi-alert-circle-outline',
              title: this.$t('errors.api.genericTitle'),
              backEnabled: '1',
              message: ''
            }
          });
        }
      } finally {
        this.retrievingWarehouse = false;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- matches the retrieveLocationMixin template method signature
    onNoGpsAllowed(cause: 'denied' | 'not-available') {
      this.calculatingLocation = false;
      this.$router.push('/gps-error');
    }
  }
});
</script>

<style scoped>
.page {
  background-color: #fcfcfc;
}

.welcome {
  font-family: Poppins, sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 125%;
  color: #3c3c3c;
}

.title {
  font-family: Poppins, sans-serif;
  font-size: 26px;
  font-style: normal;
  font-weight: 500;
  line-height: 125%;
  color: #1e3036;
}

.instructions {
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  color: #3c3c3c;
}

.recaptcha-attribution {
  font-size: 12px;
  color: #9e9e9e;
  text-align: center;
  margin-top: auto;
  padding: 0 16px 16px;
}

.recaptcha-attribution a {
  color: #9e9e9e;
}
</style>
